/**
 * C4 — "Member defaults". The values every member of this organisation starts
 * with, and whether they may change them.
 *
 * The rows are the REAL keys this organisation's policy can govern —
 * preferences/handler.py's POLICY_MANAGEABLE_KEYS, minus the two Focus controls
 * that have their own card above. That set is the honest answer to "what can an
 * organisation actually default for its members", and it is what the save button
 * writes.
 *
 * DEVIATION FROM THE FIGURE, stated rather than hidden: the figure's first row
 * is "Interface density". Density is in PERSONAL_ONLY_KEYS server-side — it
 * always follows the member and an organisation cannot default or lock it — so
 * showing it here would draw a control that returns 400 on save. The rows below
 * are the keys that are really governable.
 *
 * Every row states, in its own sub-line, the thing an admin actually needs to
 * know: whether a member may change it. That is what the lock means, and it is
 * why a lock chip and a "members may change" line always agree.
 */
export interface MemberDefaultRow {
  key: string
  label: string
  /** 'toggle' renders a switch; 'choice' renders the resolved value as text. */
  kind: 'toggle' | 'choice'
  options?: { value: string; label: string }[]
}

const MEMBER_DEFAULT_ROWS: MemberDefaultRow[] = [
  { key: 'notifications_email', label: 'Email notifications', kind: 'toggle' },
  { key: 'notifications_inapp', label: 'In-app notifications', kind: 'toggle' },
  { key: 'disclosure_receipts', label: 'Disclosure receipts', kind: 'toggle' },
  {
    key: 'analysis_depth', label: 'Analysis depth', kind: 'choice',
    options: [{ value: 'standard', label: 'Standard' }, { value: 'deep', label: 'Deep' }],
  },
  {
    key: 'citation_style', label: 'Citation style', kind: 'choice',
    options: [
      { value: 'apa', label: 'APA' }, { value: 'ieee', label: 'IEEE' },
      { value: 'harvard', label: 'Harvard' },
    ],
  },
]

export interface MemberDefaultsPanelProps {
  /** The organisation's own values; a key it has not set falls to the layers below. */
  orgPrefs: Record<string, unknown>
  lockedKeys: string[]
  /** The values members actually resolve to right now. */
  resolved: Record<string, unknown>
  saved: boolean
  onChange: (key: string, value: unknown) => void
  onLockChange: (key: string, locked: boolean) => void
}

function optionLabel(row: MemberDefaultRow, value: unknown): string {
  const found = row.options?.find((o) => o.value === value)
  return found?.label ?? (value === undefined || value === null || value === '' ? '—' : String(value))
}

export default function MemberDefaultsPanel({
  orgPrefs, lockedKeys, resolved, saved, onChange, onLockChange,
}: MemberDefaultsPanelProps) {
  const locked = new Set(lockedKeys)

  return (
    <div data-part="MemberDefaultsPanel" className="light-card p-4 lg:p-5">
      <div className="mb-2.5 flex items-center justify-between gap-2">
        <h3 className="text-[11px] font-bold uppercase tracking-wider text-iflow-700">
          Member defaults
        </h3>
        {saved && (
          <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
            Saved
          </span>
        )}
      </div>

      {MEMBER_DEFAULT_ROWS.map((row) => {
        const isLocked = locked.has(row.key)
        const value = row.key in orgPrefs ? orgPrefs[row.key] : resolved[row.key]
        return (
          <div
            key={row.key}
            className="flex items-center gap-3 border-t border-gray-100 py-2.5 first-of-type:border-t-0 first-of-type:pt-0.5"
          >
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-semibold text-gray-900">{row.label}</div>
              <div className="mt-0.5 text-[11.5px] text-gray-500">
                {row.kind === 'choice' ? `${optionLabel(row, value)} · ` : ''}
                {isLocked ? 'locked — members cannot change it' : 'members may change'}
              </div>
            </div>

            {row.kind === 'toggle' ? (
              <button
                type="button"
                role="switch"
                aria-checked={Boolean(value)}
                aria-label={row.label}
                onClick={() => onChange(row.key, !value)}
                className={`relative h-[22px] w-[40px] shrink-0 rounded-full transition-colors ${
                  value ? 'bg-iflow-500' : 'bg-gray-300'}`}
              >
                <span className={`absolute top-[2px] ${value ? 'right-[2px]' : 'left-[2px]'} h-[18px] w-[18px] rounded-full bg-white shadow`} />
              </button>
            ) : (
              <select
                aria-label={row.label}
                value={String(value ?? '')}
                onChange={(e) => onChange(row.key, e.target.value)}
                className="light-input shrink-0 rounded-lg px-2.5 py-1 text-[12px]"
              >
                {row.options?.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            )}

            <button
              type="button"
              onClick={() => onLockChange(row.key, !isLocked)}
              title={isLocked
                ? 'Unlock: a member may set their own value again'
                : 'Lock: this value overrides every member’s personal choice'}
              className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                isLocked ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`}
            >
              {isLocked ? '🔒 Locked' : 'Unlocked'}
            </button>
          </div>
        )
      })}
    </div>
  )
}
